import axios from 'axios';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from "../utils/logger";

// This is a mock API service for testing when the backend is not available
class MockApiService {
  private mockResponses: Record<string, any> = {
    // Mock auth endpoints
    '/auth/login': {
      access_token: 'mock-token-xxx',
      token_type: 'bearer',
      user: {
        id: 1,
        email: 'admin@example.com',
        full_name: 'Admin User',
        is_active: true,
        is_company_admin: true
      }
    },
    '/auth/me': {
      id: 1,
      email: 'admin@example.com',
      full_name: 'Admin User',
      is_active: true,
      is_company_admin: true,
      company_id: 1
    },
    
    // Mock company endpoints
    '/companies': [
      {
        id: 1,
        name: 'TechCorp Inc.',
        tax_id: 'US123456789',
        address_line1: '123 Tech Way',
        city: 'San Francisco',
        state: 'CA',
        zip_code: '94105',
        country: 'US',
        is_active: true,
        tax_code: 'txcd_us_23456',
        is_head_office: true,
        created_at: '2023-01-01T00:00:00Z'
      },
      {
        id: 2,
        name: 'Retail Outlet',
        tax_id: 'US987654321',
        address_line1: '456 Commerce Dr',
        city: 'Chicago',
        state: 'IL',
        zip_code: '60007',
        country: 'US',
        is_active: true,
        tax_code: 'txcd_us_12345',
        is_head_office: false,
        created_at: '2023-02-15T00:00:00Z'
      },
      {
        id: 3,
        name: 'Service Provider LLC',
        tax_id: 'US456789123',
        address_line1: '789 Service Rd',
        city: 'Miami',
        state: 'FL',
        zip_code: '33101',
        country: 'US',
        is_active: true,
        tax_code: 'txcd_us_34567',
        is_head_office: true,
        created_at: '2023-03-22T00:00:00Z'
      },
      {
        id: 4,
        name: 'Software Solutions',
        tax_id: 'US789123456',
        address_line1: '321 App Street',
        city: 'Austin',
        state: 'TX',
        zip_code: '73301',
        country: 'US',
        is_active: true,
        tax_code: 'txcd_us_45678',
        is_head_office: true,
        created_at: '2023-04-10T00:00:00Z'
      },
      {
        id: 5,
        name: 'Fashion Retailer',
        tax_id: 'US321654987',
        address_line1: '555 Style Ave',
        city: 'New York',
        state: 'NY',
        zip_code: '10001',
        country: 'US',
        is_active: true,
        tax_code: 'txcd_us_56789',
        is_head_office: true,
        created_at: '2023-05-05T00:00:00Z'
      }
    ],
    
    // Mock tax codes endpoints
    '/tax-codes': [
      { id: 'txcd_us_12345', name: 'Physical Good', description: 'General physical good', category_type: 'Physical goods' },
      { id: 'txcd_us_23456', name: 'Digital Product', description: 'General digital product', category_type: 'Digital products' },
      { id: 'txcd_us_34567', name: 'Service', description: 'General service', category_type: 'Services' },
      { id: 'txcd_us_45678', name: 'Software', description: 'Software product', category_type: 'Digital products' },
      { id: 'txcd_us_56789', name: 'Clothing', description: 'Clothing item', category_type: 'Physical goods' }
    ],
    '/tax-codes/categories': ['Physical goods', 'Digital products', 'Services'],
    
    // Mock company users
    '/company/me/users': [
      {
        id: 1,
        email: 'admin@example.com',
        full_name: 'Admin User',
        is_active: true,
        company_id: 1,
        created_at: '2023-01-01T00:00:00Z'
      },
      {
        id: 2,
        email: 'user@example.com',
        full_name: 'Regular User',
        is_active: true,
        company_id: 1,
        created_at: '2023-01-01T00:00:00Z'
      }
    ]
  };

  private axiosInstance: AxiosInstance;

  constructor() {
    // Create a real axios instance that will be intercepted
    this.axiosInstance = axios.create({
      baseURL: '/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 1000,
    });

    // Add request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        logger.log('[MOCK] Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        logger.error('[MOCK] Request error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor to mock responses
    this.axiosInstance.interceptors.response.use(
      (response) => {
        logger.log('[MOCK] Response from', response.config.url, ':', response.data);
        return response;
      },
      async (error) => {
        logger.error('[MOCK] Response error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Mock the axios methods
  async get(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    logger.log('[MOCK] GET', url);
    await this.delay(300); // Simulate network delay
    
    // Find the matching mock response
    const mockData = this.findMockResponse(url);
    
    if (mockData) {
      return {
        data: mockData,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: config || {}
      } as AxiosResponse;
    }
    
    // If no mock found, throw 404
    const error = new Error(`[MOCK] Not found: ${url}`) as any;
    error.response = {
      status: 404,
      data: {
        detail: `[MOCK] Not found: ${url}`
      }
    };
    throw error;
  }

  async post(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    logger.log('[MOCK] POST', url, data);
    await this.delay(300); // Simulate network delay
    
    // Special handling for login
    if (url === '/auth/login') {
      let username: string | null = null;
      let password: string | null = null;
      
      // Handle both URLSearchParams and plain objects
      if (data instanceof URLSearchParams) {
        username = data.get('username');
        password = data.get('password');
      } else if (typeof data === 'object') {
        username = data.username;
        password = data.password;
      }
      
      logger.log('[MOCK] Login attempt:', { username, password });
      
      // Check credentials
      if (username === 'admin@example.com' && password === 'admin123') {
        return {
          data: this.mockResponses['/auth/login'],
          status: 200,
          statusText: 'OK',
          headers: {},
          config: config || {}
        } as AxiosResponse;
      } else {
        const error = new Error('Incorrect username or password') as any;
        error.response = {
          status: 401,
          data: {
            detail: 'Incorrect username or password'
          }
        };
        throw error;
      }
    }
    
    // Handle tax code sync endpoint
    if (url === '/tax-codes/sync') {
      logger.log('[MOCK] Handling tax code sync');
      const syncResponse = {
        success: true,
        count: this.mockResponses['/tax-codes'].length,
        message: `Successfully synced ${this.mockResponses['/tax-codes'].length} tax codes`,
        sync_time: new Date().toISOString()
      };
      
      return {
        data: syncResponse,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: config || {}
      } as AxiosResponse;
    }
    
    // For other endpoints, just return success with the post data
    return {
      data: {
        id: 999,
        ...data,
        created_at: new Date().toISOString()
      },
      status: 201,
      statusText: 'Created',
      headers: {},
      config: config || {}
    } as AxiosResponse;
  }

  async put(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    logger.log('[MOCK] PUT', url, data);
    await this.delay(300); // Simulate network delay
    
    return {
      data: {
        ...data,
        updated_at: new Date().toISOString()
      },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: config || {}
    } as AxiosResponse;
  }

  async delete(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    logger.log('[MOCK] DELETE', url);
    await this.delay(300); // Simulate network delay
    
    return {
      data: { success: true },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: config || {}
    } as AxiosResponse;
  }

  // Helper to find mock response for a given URL
  private findMockResponse(url: string): any {
    logger.log('[MOCK] Looking for mock response for', url);
    
    // Handle companies list with pagination
    if (url.startsWith('/companies?') || url === '/companies') {
      const companies = this.mockResponses['/companies'];
      // Return in paginated format
      return {
        items: companies,
        total: companies.length,
        page: 1,
        size: companies.length
      };
    }
    
    // Direct match
    if (this.mockResponses[url]) {
      return this.mockResponses[url];
    }
    
    // Check for path parameters (e.g. /companies/1)
    const parts = url.split('/');
    if (parts.length > 2) {
      const basePath = parts.slice(0, 2).join('/');
      const id = parts[2];
      
      if (basePath === '/companies' && this.mockResponses['/companies']) {
        const companies = this.mockResponses['/companies'];
        return companies.find((c: any) => c.id.toString() === id);
      }
    }
    
    return null;
  }

  // Helper to simulate network delay
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Set up the default headers
  defaults = {
    headers: {
      common: {} as Record<string, string>
    },
    baseURL: '/api/v1'
  };
}

export const mockApi = new MockApiService(); 